/**
 * Securities — PMS securities master (read + in-app edit).
 *
 * A wide, Excel-like grid: attribute columns + per-tier CLASSIFICATION columns
 * (derived live from the taxonomy), leftmost columns FROZEN (user-adjustable),
 * sticky header, the whole set on one page. Per column a header POPOVER gives
 * sort + a type-appropriate filter. Columns can be hidden/revealed (Columns
 * control) and the layout is remembered locally. Filtering + sorting are
 * CLIENT-SIDE over the whole set (the Billing-table pattern).
 *
 * Filter SHAPE — deliberate divergence (§3h/C-178, Pedro 2026-07-21): this wide
 * reference grid uses per-column header POPOVERS; the app's other (narrow) lists
 * use the always-visible ColumnFilters second row. Both run during Beta.
 *
 * Classification columns are PER-TIER (Pedro 2026-07-22): each dimension shows one
 * column per tier (e.g. Asset Class + Sub-Class; Development/Region/Country); the
 * cell holds that tier's value from the security's reading (parents implied by
 * roll-up). Read-only here — inline classification editing is a later increment.
 *
 * WRITE side (Inc 1, C-294), gated `securities.manage`: Edit mode = a batched
 * transaction (§3g Pattern A) — click an attribute cell to edit inline, edits
 * stage (orange) + commit on one Save, dirty-guarded; frozen identifiers
 * (ISIN, currency, BB Ticker, FIGI) are locked. New security = a create form
 * (duplicate refused loudly). Delete = a per-row action (own confirm, soft-delete).
 *
 * Access: admin-only page gate for now; edit affordances gate on `securities.manage`.
 * Design: decisions/2026-07-22_securities-master-write-side.md.
 */
import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon, Alert, Anchor, Badge, Button, Checkbox, Container, Divider, Drawer, Grid, Group,
  Loader, Modal, MultiSelect, Popover, Select, SimpleGrid, Stack, Table, Text, TextInput, Title, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle, IconArrowBackUp, IconChevronDown, IconList, IconChevronRight, IconDownload, IconEdit, IconFilter, IconPlus, IconSearch,
  IconSortAscending, IconSortDescending, IconTrash, IconUpload,
} from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { classificationApi, type ClassificationValue } from '../api/classification';
import { securityUsageApi, type SecurityUsage } from '../api/models';
import {
  securitiesApi, type SecuritiesImportConflict, type SecuritiesImportReport,
  type SecurityCreatePayload, type SecurityDetail, type SecurityRow,
  type SecurityUpdatePayload,
} from '../api/securities';
import { AccessDeniedAlert } from '../components/RequireAccess';
import { BackToCompliance } from '../components/BackToCompliance';
import { ColumnsPicker } from '../components/ColumnsPicker';
import { ColumnResizeHandle } from '../components/ColumnResizeHandle';
import { ordered, useColumnOrder, useColumnWidths, useHeadingDrag } from '../components/gridColumnOrder';
import {
  allGroupIds, groupRowBg, groupRows, levelKey, levelOptions, validLevels, type GroupLens, type GroupNode,
} from '../utils/portfolioGrouping';
import {
  ColumnMenu, filterActive, type FilterValue, type Filters, type Ft, type SortState,
} from '../components/GridColumnMenu';
import { StatusExplained } from '../components/StatusExplained';
import { ADMIN_PAGE_READ_ROLES } from '../api/types';
import { useDirtyForm } from '../components/DirtyFormProvider';
import { XlsxDropzone } from '../components/XlsxDropzone';
import { DisabledTooltipButton } from '../components/DisabledTooltipButton';
import { ShowWhereHeld, WhereHeldList } from '../components/WhereHeldList';
import { useCurrentUser } from '../hooks/useAuth';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { usePermissions } from '../hooks/usePermissions';

const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong.');
// PX-GRAPH: the chart pulls the Vega family, so it loads only when "(graph)" is clicked (C-260).
const PriceHistoryChart = lazy(() => import('../components/PriceHistoryChart'));
const fmtNum = (n: number | null | undefined) =>
  n == null ? '' : n.toLocaleString(undefined, { maximumFractionDigits: 6 });
const fmt2 = (n: number | null | undefined) =>
  n == null ? '' : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Columns shown with a fixed 2 decimals (percentage-style commercials).
const TWO_DP = new Set(['management_fee', 'ter']);
const dash = (s: string | null | undefined) => (s && String(s).trim() ? String(s) : '—');

interface Col {
  key: string; label: string; width: number; ft: Ft; group: string;
  field?: keyof SecurityRow; num?: boolean; bool?: boolean;
  code?: string; tierLevel?: number; dimName?: string;  // classification (lens) columns
  /** A column computed in the page, not stored (the Classification status, HP-V5). */
  derived?: (row: SecurityRow) => string;
  /** The value a column sorts by when it is not its text (the status: least to most complete). */
  sortValue?: (row: SecurityRow) => number;
}

// The Classification status column (HP-V5, Pedro 2026-10-19, option A): read from the SAVED
// readings, like the counts above the table. Not part of the Excel export (server-side).
const CLS_STATUS_KEY = 'cls_status';
const NON = 'Non-classified';
const PARTLY = 'Partly classified';
const FULLY = 'Fully classified';

// Attribute columns, split HEAD / TAIL; per-tier classification columns are inserted
// between them at render (derived from the taxonomy). attr key = field name; the
// classification key is "cls:{dimension_code}:{tier_level}" (matches the backend export).
const ATTR_HEAD: Col[] = [
  { key: 'isin', label: 'ISIN', width: 150, ft: 'text', field: 'isin', group: 'Identity' },
  { key: 'currency', label: 'Currency', width: 90, ft: 'enum', field: 'currency', group: 'Identity' },
  { key: 'bb_ticker', label: 'BB Ticker', width: 150, ft: 'text', field: 'bb_ticker', group: 'Identity' },
  { key: 'description', label: 'Name', width: 280, ft: 'text', field: 'description', group: 'Identity' },
  // TRADE-1 (Pedro 2026-10-22): may be bought / kept in a trade; ticked in Edit mode, never empty.
  { key: 'authorized', label: 'Approved', width: 100, ft: 'enum', field: 'authorized', bool: true, group: 'Governance' },
  { key: 'fund_type', label: 'Fund Type', width: 110, ft: 'enum', field: 'fund_type', group: 'Identity' },
  { key: 'is_benchmark', label: 'Benchmark', width: 100, ft: 'enum', field: 'is_benchmark', bool: true, group: 'Benchmark' },
];
const ATTR_TAIL: Col[] = [
  { key: 'issuer', label: 'Issuer', width: 220, ft: 'text', field: 'issuer', group: 'Instrument terms' },
  { key: 'issuer_industry_text', label: 'Issuer industry', width: 200, ft: 'text', field: 'issuer_industry_text', group: 'Instrument terms' },
  { key: 'coupon_rate', label: 'Coupon rate', width: 110, ft: 'num', field: 'coupon_rate', num: true, group: 'Instrument terms' },
  { key: 'issue_date', label: 'Issue date', width: 120, ft: 'date', field: 'issue_date', group: 'Instrument terms' },
  { key: 'first_coupon_date', label: '1st coupon', width: 120, ft: 'date', field: 'first_coupon_date', group: 'Instrument terms' },
  { key: 'maturity_date', label: 'Maturity', width: 120, ft: 'date', field: 'maturity_date', group: 'Instrument terms' },
  { key: 'call_date', label: 'Call date', width: 120, ft: 'date', field: 'call_date', group: 'Instrument terms' },
  { key: 'rating', label: 'Rating', width: 90, ft: 'enum', field: 'rating', group: 'Rating & risk' },
  { key: 'rating_agency', label: 'Rating agency', width: 150, ft: 'enum', field: 'rating_agency', group: 'Rating & risk' },
  { key: 'rating_date', label: 'Rating date', width: 120, ft: 'date', field: 'rating_date', group: 'Rating & risk' },
  { key: 'esma_risk', label: 'ESMA risk', width: 150, ft: 'enum', field: 'esma_risk', group: 'Rating & risk' },
  { key: 'sfdr', label: 'SFDR', width: 80, ft: 'enum', field: 'sfdr', group: 'Rating & risk' },
  { key: 'mifid_complex', label: 'MiFID complex', width: 120, ft: 'enum', field: 'mifid_complex', bool: true, group: 'MiFID & venue' },
  { key: 'mifid_complex_attribute', label: 'MiFID complex attribute', width: 200, ft: 'text', field: 'mifid_complex_attribute', group: 'MiFID & venue' },
  { key: 'mifid_firds_indicator', label: 'MiFID FIRDS', width: 110, ft: 'enum', field: 'mifid_firds_indicator', group: 'MiFID & venue' },
  { key: 'mifid_admitted_to_trading', label: 'Admitted to trading', width: 140, ft: 'enum', field: 'mifid_admitted_to_trading', bool: true, group: 'MiFID & venue' },
  { key: 'primary_exchange_mic', label: 'Primary exchange MIC', width: 150, ft: 'enum', field: 'primary_exchange_mic', group: 'MiFID & venue' },
  { key: 'trading_venue_mic', label: 'Trading venue MIC', width: 150, ft: 'enum', field: 'trading_venue_mic', group: 'MiFID & venue' },
  { key: 'management_fee', label: 'Management fee', width: 120, ft: 'num', field: 'management_fee', num: true, group: 'Commercials' },
  { key: 'ter', label: 'TER', width: 90, ft: 'num', field: 'ter', num: true, group: 'Commercials' },
  { key: 'min_piece', label: 'Min piece', width: 120, ft: 'num', field: 'min_piece', num: true, group: 'Commercials' },
  { key: 'min_increment', label: 'Min increment', width: 120, ft: 'num', field: 'min_increment', num: true, group: 'Commercials' },
  { key: 'lei', label: 'LEI', width: 180, ft: 'text', field: 'lei', group: 'Identity' },
  { key: 'figi', label: 'FIGI', width: 140, ft: 'text', field: 'figi', group: 'Identity' },
  { key: 'associated_benchmark_label', label: 'Assoc. benchmark', width: 200, ft: 'text', field: 'associated_benchmark_label', group: 'Benchmark' },
  { key: 'benchmark_name', label: 'Benchmark name', width: 240, ft: 'text', field: 'benchmark_name', group: 'Benchmark' },
  { key: 'benchmark_bb_ticker', label: 'Benchmark BB ticker', width: 160, ft: 'text', field: 'benchmark_bb_ticker', group: 'Benchmark' },
  { key: 'latest_price', label: 'Latest price', width: 110, ft: 'num', field: 'latest_price', num: true, group: 'Price' },
  { key: 'latest_price_date', label: 'Latest price date', width: 130, ft: 'date', field: 'latest_price_date', group: 'Price' },
  { key: 'comments', label: 'Comments', width: 240, ft: 'text', field: 'comments', group: 'Housekeeping' },
];

// ── editability (Inc 1) ─────────────────────────────────────────────────────────
type EditKind = 'text' | 'num' | 'date' | 'bool';
const FROZEN = new Set(['isin', 'currency', 'bb_ticker', 'figi']);
const DERIVED = new Set(['associated_benchmark_label', 'latest_price', 'latest_price_date']);
// The Approved column is a tick box (TRADE-1): clicked, not opened in an editor.
const APPROVED_KEY = 'authorized';
const APPROVED_TIP = 'Approved: may be chosen to buy, or kept, in a trade. Every security starts not approved.';
function editKind(c: Col): EditKind | null {
  if (!c.field || c.ft === 'lens' || FROZEN.has(c.key) || DERIVED.has(c.key)) return null;
  if (c.bool) return 'bool';
  if (c.num) return 'num';
  if (c.ft === 'date') return 'date';
  return 'text';
}

// The value of a per-tier classification column for a row (path[tier-1] or '').
function tierValue(col: Col, row: SecurityRow): string {
  const r = row.classifications.find((c) => c.dimension_code === col.code);
  const lvl = col.tierLevel ?? 1;
  return r && r.path.length >= lvl ? r.path[lvl - 1] : '';
}

function cellText(col: Col, row: SecurityRow): string {
  if (col.derived) return col.derived(row);
  if (col.ft === 'lens') return tierValue(col, row) || '—';
  const v = row[col.field!];
  if (col.bool) return v == null ? '—' : v ? 'Yes' : 'No';
  if (col.num) return v == null ? '—' : (TWO_DP.has(col.key) ? fmt2(v as number) : fmtNum(v as number));
  return dash(v as string | null);
}

type Drafts = Record<string, Partial<SecurityRow>>;
type ClsDrafts = Record<string, Record<string, string | null>>;

/** `base` with `field` of security `id` staged as `val` (or un-staged when it equals the saved
 *  `orig`); the SAME object when nothing changes, so a no-op is not an undo step. */
function stageField(base: Drafts, id: string, field: string, val: unknown, orig: unknown): Drafts {
  const cur0 = (base[id] ?? {}) as Record<string, unknown>;
  const had = Object.prototype.hasOwnProperty.call(cur0, field);
  const same = (val ?? null) === (orig ?? null);
  if (same ? !had : had && cur0[field] === val) return base;
  const cur = { ...cur0 };
  if (same) delete cur[field]; else cur[field] = val;
  const n = { ...base };
  if (Object.keys(cur).length === 0) delete n[id]; else n[id] = cur as Partial<SecurityRow>;
  return n;
}

// ── detail drawer ──────────────────────────────────────────────────────────────
function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return <div><Text size="xs" c="dimmed">{label}</Text><Text size="sm">{value ?? '—'}</Text></div>;
}
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Stack gap="xs">
      <Text fw={600} size="sm">{title}</Text>
      <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="sm">{children}</SimpleGrid>
    </Stack>
  );
}
function DetailBody({ d }: { d: SecurityDetail }) {
  const { can } = usePermissions();
  const canGraph = can('portfolios.read');
  const [graphOpen, setGraphOpen] = useState(false);
  return (
    <Stack gap="lg">
      <Modal opened={graphOpen} onClose={() => setGraphOpen(false)} size="xl" centered
        title={<Text fw={600}>Price · {d.description}</Text>}>
        {graphOpen && (
          <Suspense fallback={<Loader size="sm" />}>
            <PriceHistoryChart securityId={d.id} />
          </Suspense>
        )}
      </Modal>
      <Group gap="xs">
        <Tooltip withArrow label={APPROVED_TIP}>
          {d.authorized
            ? <Badge color="green" variant="light">Approved</Badge>
            : <Badge color="gray" variant="light">Not approved</Badge>}
        </Tooltip>
        {d.is_benchmark && <Badge color="azul" variant="light">Benchmark</Badge>}
        {d.is_deleted && (
          <StatusExplained entity="security" status="Deleted">
            <Badge color="red" variant="light">Deleted</Badge>
          </StatusExplained>
        )}
        <Badge variant="light">{d.currency}</Badge>
        {d.fund_type && <Badge variant="outline">{d.fund_type}</Badge>}
      </Group>
      <Section title="Identity">
        <Field label="ISIN" value={d.isin} />
        <Field label="BB Ticker" value={dash(d.bb_ticker)} />
        <Field label="LEI" value={dash(d.lei)} />
        <Field label="FIGI" value={dash(d.figi)} />
      </Section>
      <Section title="Classifications">
        {d.classifications.length === 0
          ? <Text size="sm" c="dimmed">No classifications tagged.</Text>
          : d.classifications.map((c) => (
              <Field key={c.dimension_code} label={c.dimension_name} value={c.path.join(' › ')} />
            ))}
      </Section>
      <Section title="Instrument terms">
        <Field label="Issuer" value={dash(d.issuer)} />
        <Field label="Industry" value={dash(d.issuer_industry_text)} />
        <Field label="Coupon rate" value={d.coupon_rate == null ? '—' : `${fmtNum(d.coupon_rate)}%`} />
        <Field label="Issue date" value={dash(d.issue_date)} />
        <Field label="1st coupon" value={dash(d.first_coupon_date)} />
        <Field label="Maturity" value={dash(d.maturity_date)} />
        <Field label="Call date" value={dash(d.call_date)} />
      </Section>
      <Section title="Rating & risk">
        <Field label="Rating" value={dash(d.rating)} />
        <Field label="Agency" value={dash(d.rating_agency)} />
        <Field label="Rating date" value={dash(d.rating_date)} />
        <Field label="ESMA risk" value={dash(d.esma_risk)} />
        <Field label="SFDR" value={dash(d.sfdr)} />
      </Section>
      <Section title="MiFID & venue">
        <Field label="Complex?" value={d.mifid_complex == null ? '—' : d.mifid_complex ? 'Yes' : 'No'} />
        <Field label="Complex attribute" value={dash(d.mifid_complex_attribute)} />
        <Field label="FIRDS indicator" value={dash(d.mifid_firds_indicator)} />
        <Field label="Admitted to trading" value={d.mifid_admitted_to_trading == null ? '—' : d.mifid_admitted_to_trading ? 'Yes' : 'No'} />
        <Field label="Primary exchange (MIC)" value={dash(d.primary_exchange_mic)} />
        <Field label="Trading venue (MIC)" value={dash(d.trading_venue_mic)} />
      </Section>
      <Section title="Commercials">
        <Field label="Management fee" value={d.management_fee == null ? '—' : `${fmt2(d.management_fee)}%`} />
        <Field label="TER" value={d.ter == null ? '—' : `${fmt2(d.ter)}%`} />
        <Field label="Min piece" value={dash(fmtNum(d.min_piece))} />
        <Field label="Min increment" value={dash(fmtNum(d.min_increment))} />
      </Section>
      <Section title="Benchmark">
        <Field label="Associated benchmark" value={dash(d.associated_benchmark_label ?? d.benchmark_name)} />
        <Field label="Benchmark BB ticker" value={dash(d.benchmark_bb_ticker)} />
      </Section>
      <Stack gap="xs">
        <Group gap={6}>
          <Text fw={600} size="sm">Prices ({d.prices.length})</Text>
          {/* PX-GRAPH (Pedro 2026-10-26): "(graph)" beside the word, for whoever reads portfolios
              (the custodian prices come from client positions files). */}
          {canGraph && (
            <Tooltip withArrow multiline w={300} label="The price over time: the custodians' prices from the stored positions files, and the Securities Master's price.">
              <Anchor size="sm" onClick={() => setGraphOpen(true)}>(graph)</Anchor>
            </Tooltip>
          )}
        </Group>
        {d.prices.length === 0 ? (
          <Text size="sm" c="dimmed">No prices.</Text>
        ) : (
          <Table striped withTableBorder>
            <Table.Thead><Table.Tr>
              <Table.Th>Date</Table.Th><Table.Th>Price</Table.Th><Table.Th>Var.</Table.Th>
              <Table.Th>Source</Table.Th><Table.Th>Ccy</Table.Th>
            </Table.Tr></Table.Thead>
            <Table.Tbody>
              {d.prices.map((p) => (
                <Table.Tr key={p.quote_date}>
                  <Table.Td>{p.quote_date}</Table.Td>
                  <Table.Td>{dash(fmtNum(p.price))}</Table.Td>
                  <Table.Td>{dash(fmtNum(p.variation))}</Table.Td>
                  <Table.Td>{dash(p.quote_source)}</Table.Td>
                  <Table.Td>{dash(p.quote_currency)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
      {d.comments && (
        <Stack gap={2}><Text fw={600} size="sm">Comments</Text><Text size="sm">{d.comments}</Text></Stack>
      )}
    </Stack>
  );
}
function DetailDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['securities', 'detail', id],
    queryFn: () => securitiesApi.get(id!),
    enabled: id !== null,
  });
  return (
    <Drawer opened={id !== null} onClose={onClose} position="right" size="xl"
      title={q.data ? q.data.description : 'Security'}>
      {q.isLoading && <Loader />}
      {q.isError && <Alert color="red">{errMsg(q.error)}</Alert>}
      {q.data && <DetailBody d={q.data} />}
    </Drawer>
  );
}

// ── create drawer (Inc 1) ────────────────────────────────────────────────────
interface FieldSpec {
  key: keyof SecurityCreatePayload; label: string; kind: EditKind; required?: boolean;
  /** A Yes / No that is never empty: starts at No (Approved). */
  yesNo?: boolean;
}
const CREATE_SECTIONS: { title: string; fields: FieldSpec[] }[] = [
  { title: 'Identity', fields: [
    { key: 'isin', label: 'ISIN', kind: 'text', required: true },
    { key: 'currency', label: 'Currency', kind: 'text', required: true },
    { key: 'description', label: 'Name', kind: 'text', required: true },
    { key: 'authorized', label: 'Approved', kind: 'bool', yesNo: true },
    { key: 'bb_ticker', label: 'BB Ticker', kind: 'text' },
    { key: 'lei', label: 'LEI', kind: 'text' },
    { key: 'figi', label: 'FIGI', kind: 'text' },
    { key: 'fund_type', label: 'Fund type', kind: 'text' },
  ] },
  { title: 'Benchmark', fields: [
    { key: 'is_benchmark', label: 'Is a benchmark', kind: 'bool' },
    { key: 'benchmark_name', label: 'Associated benchmark (name)', kind: 'text' },
    { key: 'benchmark_bb_ticker', label: 'Benchmark BB ticker', kind: 'text' },
  ] },
  { title: 'Instrument terms', fields: [
    { key: 'issuer', label: 'Issuer', kind: 'text' },
    { key: 'issuer_industry_text', label: 'Issuer industry', kind: 'text' },
    { key: 'coupon_rate', label: 'Coupon rate (%)', kind: 'num' },
    { key: 'issue_date', label: 'Issue date', kind: 'date' },
    { key: 'first_coupon_date', label: '1st coupon date', kind: 'date' },
    { key: 'maturity_date', label: 'Maturity date', kind: 'date' },
    { key: 'call_date', label: 'Call date', kind: 'date' },
  ] },
  { title: 'Rating & risk', fields: [
    { key: 'rating', label: 'Rating', kind: 'text' },
    { key: 'rating_agency', label: 'Rating agency', kind: 'text' },
    { key: 'rating_date', label: 'Rating date', kind: 'date' },
    { key: 'esma_risk', label: 'ESMA risk', kind: 'text' },
    { key: 'sfdr', label: 'SFDR', kind: 'text' },
  ] },
  { title: 'MiFID & venue', fields: [
    { key: 'mifid_complex', label: 'MiFID complex', kind: 'bool' },
    { key: 'mifid_complex_attribute', label: 'MiFID complex attribute', kind: 'text' },
    { key: 'mifid_firds_indicator', label: 'MiFID FIRDS indicator', kind: 'text' },
    { key: 'mifid_admitted_to_trading', label: 'Admitted to trading', kind: 'bool' },
    { key: 'primary_exchange_mic', label: 'Primary exchange MIC', kind: 'text' },
    { key: 'trading_venue_mic', label: 'Trading venue MIC', kind: 'text' },
  ] },
  { title: 'Commercials', fields: [
    { key: 'management_fee', label: 'Management fee (%)', kind: 'num' },
    { key: 'ter', label: 'TER (%)', kind: 'num' },
    { key: 'min_piece', label: 'Min piece', kind: 'num' },
    { key: 'min_increment', label: 'Min increment', kind: 'num' },
  ] },
  { title: 'Comments', fields: [
    { key: 'comments', label: 'Comments', kind: 'text' },
  ] },
];
const BOOL_DATA = [{ value: '', label: '—' }, { value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }];

function CreateDrawer({ opened, onClose, onCreated }: { opened: boolean; onClose: () => void; onCreated: () => void }) {
  const [vals, setVals] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const set = (k: string, v: string) => setVals((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    const isin = (vals.isin ?? '').trim();
    const currency = (vals.currency ?? '').trim();
    const description = (vals.description ?? '').trim();
    if (!isin || !currency || !description) { setErr('ISIN, currency and name are required.'); return; }
    const body: Record<string, unknown> = { isin, currency, description };
    for (const sec of CREATE_SECTIONS) {
      for (const f of sec.fields) {
        if (f.key === 'isin' || f.key === 'currency' || f.key === 'description') continue;
        const raw = (vals[f.key] ?? '').trim();
        if (f.kind === 'bool') { if (raw !== '') body[f.key] = raw === 'true'; }
        else if (f.kind === 'num') { if (raw !== '' && !Number.isNaN(Number(raw))) body[f.key] = Number(raw); }
        else if (raw !== '') body[f.key] = raw;
      }
    }
    setSaving(true); setErr(null);
    try {
      await securitiesApi.create(body as unknown as SecurityCreatePayload);
      notifications.show({ color: 'green', title: 'Security added', message: description });
      setVals({});
      onCreated();
      onClose();
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer opened={opened} onClose={onClose} position="right" size="lg" title="New security">
      <Stack>
        {err && <Alert color="red" title="Could not add">{err}</Alert>}
        <Text size="xs" c="dimmed">
          Identity (ISIN, currency, name) is set once and cannot be changed later. You can fill
          the rest now or edit the row afterwards.
        </Text>
        {CREATE_SECTIONS.map((sec) => (
          <Stack key={sec.title} gap="xs">
            <Text fw={600} size="sm">{sec.title}</Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              {sec.fields.map((f) => (
                f.kind === 'bool' ? (
                  <Select key={f.key} label={f.label} data={f.yesNo ? BOOL_DATA.slice(1) : BOOL_DATA}
                    allowDeselect={false}
                    value={vals[f.key] ?? (f.yesNo ? 'false' : '')} onChange={(v) => set(f.key, v ?? '')} />
                ) : (
                  <TextInput key={f.key} label={f.label} required={f.required} withAsterisk={f.required}
                    type={f.kind === 'num' ? 'number' : f.kind === 'date' ? 'date' : 'text'}
                    value={vals[f.key] ?? ''} onChange={(e) => set(f.key, e.currentTarget.value)} />
                )
              ))}
            </SimpleGrid>
          </Stack>
        ))}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} loading={saving}>Add security</Button>
        </Group>
      </Stack>
    </Drawer>
  );
}

// ── attributes upload drawer (Inc 3b-i) ──────────────────────────────────────
const IMPORT_MODES: { value: SecuritiesImportConflict; label: string; desc: string }[] = [
  { value: 'skip', label: 'Import new', desc: 'Add securities not already in the master; leave existing ones untouched.' },
  { value: 'overwrite', label: 'Overwrite existing', desc: 'Add new + update matching securities. Blank cells keep the current value.' },
  { value: 'replace', label: 'Replace all', desc: 'Add + update, CLEAR blank cells, and DELETE securities not in the file.' },
];

function ReportRow({ label, value, color }: { label: string; value: number; color?: string }) {
  return <Group gap={6}><Badge variant="light" color={color}>{value}</Badge><Text size="sm">{label}</Text></Group>;
}

function ImportDrawer({ opened, onClose, onImported }: { opened: boolean; onClose: () => void; onImported: () => void }) {
  const [mode, setMode] = useState<SecuritiesImportConflict>('skip');
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<SecuritiesImportReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmReplace, setConfirmReplace] = useState(false);

  const reset = () => { setFile(null); setReport(null); setMode('skip'); };
  const close = () => { reset(); onClose(); };

  const runPreview = async (f: File, m: SecuritiesImportConflict) => {
    setBusy(true); setReport(null);
    try { setReport(await securitiesApi.importFile(f, 'preview', m)); }
    catch (e) { notifications.show({ color: 'red', title: 'Preview failed', message: errMsg(e) }); }
    finally { setBusy(false); }
  };
  const onSelect = (f: File) => { setFile(f); void runPreview(f, mode); };
  const onModeChange = (m: SecuritiesImportConflict) => { setMode(m); if (file) void runPreview(file, m); };

  const doCommit = async () => {
    if (!file) return;
    setConfirmReplace(false); setBusy(true);
    try {
      const r = await securitiesApi.importFile(file, 'commit', mode);
      const parts: string[] = [];
      if (r.inserted) parts.push(`${r.inserted} added`);
      if (r.updated) parts.push(`${r.updated} updated`);
      if (r.skipped) parts.push(`${r.skipped} kept`);
      if (r.deleted) parts.push(`${r.deleted} deleted`);
      notifications.show({ color: 'green', title: 'Import complete', message: parts.join(' · ') || 'No changes.' });
      onImported(); close();
    } catch (e) {
      notifications.show({ color: 'red', title: 'Import failed', message: errMsg(e) });
    } finally { setBusy(false); }
  };
  const onCommitClick = () => {
    if (mode === 'replace' && (report?.delete_count ?? 0) > 0) setConfirmReplace(true);
    else void doCommit();
  };

  const blocked = !!report && report.blocking_errors.length > 0;
  const commitLabel = mode === 'replace' ? 'Replace all' : mode === 'overwrite' ? 'Overwrite & add' : 'Import new';

  const downloadTemplate = async () => {
    try { await securitiesApi.downloadTemplate(); }
    catch (e) { notifications.show({ color: 'red', title: 'Template failed', message: errMsg(e) }); }
  };

  return (
    <Drawer opened={opened} onClose={close} position="right" size="lg" title="Upload securities (attributes)">
      <Stack>
        <Text size="xs" c="dimmed">
          Upload an Excel of securities-master attributes (not price). Match is by BB Ticker, then
          ISIN + Currency. The identity columns (ISIN, Currency, BB Ticker, FIGI) are never changed on
          an existing security — they identify it.
        </Text>
        <Button variant="subtle" size="xs" leftSection={<IconDownload size={14} />}
          style={{ alignSelf: 'flex-start' }} onClick={downloadTemplate}>
          Download blank template
        </Button>

        <Select label="Mode" allowDeselect={false}
          data={IMPORT_MODES.map((m) => ({ value: m.value, label: m.label }))}
          value={mode} onChange={(v) => onModeChange((v as SecuritiesImportConflict) ?? 'skip')} />
        <Text size="xs" c="dimmed">{IMPORT_MODES.find((m) => m.value === mode)?.desc}</Text>

        <XlsxDropzone hint="One Excel file: the Securities export or the upload template, filled in."
          onSelect={onSelect} loading={busy}
          onReject={() => notifications.show({ color: 'red', title: 'Not accepted', message: 'Please choose a single .xlsx file.' })} />
        {file && <Text size="sm">Chosen: <b>{file.name}</b></Text>}

        {busy && <Loader size="sm" />}

        {report && !busy && (
          blocked ? (
            <Alert color="red" title={`${report.blocking_errors.length} row(s) must be fixed`}>
              <Stack gap={4}>
                {report.blocking_errors.map((e, i) => <Text key={i} size="xs">{e}</Text>)}
                <Text size="xs" c="dimmed">Fix these in the file and re-upload.</Text>
              </Stack>
            </Alert>
          ) : (
            <Stack gap="xs">
              <Text fw={600} size="sm">Preview — what this upload will do</Text>
              <ReportRow label="new securities added" value={report.new_count} color="green" />
              {mode !== 'skip' && (
                <ReportRow label={`securities updated (${report.field_change_count} field changes)`} value={report.changed_count} color="orange" />
              )}
              {mode === 'skip' && report.matched_count > 0 && (
                <ReportRow label="existing securities left unchanged" value={report.matched_count} />
              )}
              {mode === 'replace' && (
                <ReportRow label="securities deleted (not in the file)" value={report.delete_count} color="red" />
              )}
              {report.new_sample.length > 0 && (
                <Text size="xs" c="dimmed">New: {report.new_sample.join(' · ')}{report.new_count > report.new_sample.length ? ' …' : ''}</Text>
              )}
              {report.changed_sample.length > 0 && (
                <Stack gap={2}>
                  <Text size="xs" c="dimmed">Changes:</Text>
                  {report.changed_sample.map((s, i) => <Text key={i} size="xs" c="dimmed">· {s}</Text>)}
                </Stack>
              )}
              {mode === 'replace' && report.delete_sample.length > 0 && (
                <Text size="xs" c="red">Will delete: {report.delete_sample.join(' · ')}{report.delete_count > report.delete_sample.length ? ' …' : ''}</Text>
              )}
              {(report.unapproved_in_use ?? []).length > 0 && (
                <Alert color="red" title={`${report.unapproved_in_use_ids.length} securit${report.unapproved_in_use_ids.length === 1 ? 'y' : 'ies'} to be un-approved ${report.unapproved_in_use_ids.length === 1 ? 'is' : 'are'} held`}>
                  <Stack gap={4}>
                    <Text size="xs">
                      This upload sets Approved to No for securities in a bucket and / or a portfolio (latest positions).
                      The buckets will show them red; they can still be used.
                    </Text>
                    {report.unapproved_in_use.map((s, i) => <Text key={i} size="xs">· {s}</Text>)}
                    <ShowWhereHeld ids={report.unapproved_in_use_ids} />
                  </Stack>
                </Alert>
              )}
              {report.errors.length > 0 && (
                <Alert color="yellow" title="Some cells were skipped">
                  {report.errors.map((e, i) => <Text key={i} size="xs">{e}</Text>)}
                </Alert>
              )}
            </Stack>
          )
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={close}>Cancel</Button>
          <Button color={mode === 'replace' ? 'red' : undefined}
            onClick={onCommitClick} loading={busy} disabled={!report || blocked}>
            {commitLabel}
          </Button>
        </Group>
      </Stack>

      <Modal opened={confirmReplace} onClose={() => setConfirmReplace(false)} title="Replace all — confirm">
        <Stack>
          <Text size="sm">
            This will <b>delete {report?.delete_count} securit{report?.delete_count === 1 ? 'y' : 'ies'}</b> not in
            the file (soft-delete) and clear blank cells. Continue?
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmReplace(false)}>Cancel</Button>
            <Button color="red" onClick={doCommit} loading={busy}>Replace all</Button>
          </Group>
        </Stack>
      </Modal>
    </Drawer>
  );
}

// The per-column header popover (sort + filter) is the shared components/GridColumnMenu.

// ── inline cell editor (native, one active cell at a time) ────────────────────
function CellEditor({ kind, initial, onCommit, onCancel }: {
  kind: EditKind; initial: string; onCommit: (raw: string) => void; onCancel: () => void;
}) {
  const style: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', font: 'inherit',
    border: '1px solid var(--mantine-color-azul-5)', borderRadius: 4, padding: '2px 4px',
  };
  if (kind === 'bool') {
    return (
      <select autoFocus defaultValue={initial} style={style}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onCommit(e.currentTarget.value)}
        onBlur={(e) => onCommit(e.currentTarget.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }}>
        <option value="">—</option><option value="true">Yes</option><option value="false">No</option>
      </select>
    );
  }
  return (
    <input autoFocus defaultValue={initial} style={style}
      type={kind === 'num' ? 'number' : kind === 'date' ? 'date' : 'text'}
      onClick={(e) => e.stopPropagation()}
      onBlur={(e) => onCommit(e.currentTarget.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        else if (e.key === 'Escape') onCancel();
      }} />
  );
}

// inline classification picker (one value per dimension; path-labelled; clearable)
function ClsCellEditor({ options, value, onCommit, onClose }: {
  options: { value: string; label: string }[]; value: string | null;
  onCommit: (v: string | null) => void; onClose: () => void;
}) {
  return (
    <Select data={options} value={value} searchable clearable autoFocus defaultDropdownOpened
      size="xs" placeholder="—" comboboxProps={{ withinPortal: true }}
      onClick={(e) => e.stopPropagation()}
      onChange={(v) => onCommit(v)}
      onDropdownClose={onClose} />
  );
}

// ── page ─────────────────────────────────────────────────────────────────────
export function SecuritiesPage() {
  useDocumentTitle('Securities');
  const { user } = useCurrentUser();
  const { can } = usePermissions();
  const qc = useQueryClient();
  const { registerDirty, confirmDiscardAsync } = useDirtyForm();
  const canManage = can('securities.manage');
  const isAdmin = !!user && user.role === 'admin';
  // P45 (Pedro 2026-10-15): RM and Compliance read the page; editing stays behind
  // securities.manage and the Excel export behind Admin.
  const canRead = !!user && ADMIN_PAGE_READ_ROLES.includes(user.role);

  // Classification-completeness filter (HP, Pedro 2026-10-18): none / non-classified only /
  // partly classified only. ?nonClassified=1 (the Portfolios page links here) opens on the first.
  const [searchParams] = useSearchParams();
  // Since HP-V5 the two buttons above the table are shortcuts for the Classification column's
  // filter (one filter, so they never disagree; a deliberate exception to C-132, app-language D10).
  // ?notApproved=1 (View portfolio's "not approved" counter, Pedro 2026-10-23) opens on the
  // Approved column filtered to No.
  const [filters, setFilters] = useState<Filters>(() =>
    (searchParams.get('nonClassified') === '1' ? { [CLS_STATUS_KEY]: [NON] }
      : searchParams.get('notApproved') === '1' ? { authorized: ['No'] } : {}));
  // M1-LINK2 (Pedro 2026-10-26): a link from a bucket also fills Search (`?q=<ISIN>`), so the
  // list shows that security behind its detail drawer.
  const [search, setSearch] = useState(() => searchParams.get('q') ?? '');
  const clsPick = (filters[CLS_STATUS_KEY] as string[] | undefined) ?? [];
  const onlyStatus = (s: string) => clsPick.length === 1 && clsPick[0] === s;
  const toggleStatusShortcut = (s: string) =>
    setFilters((p) => {
      const n = { ...p };
      if (onlyStatus(s)) delete n[CLS_STATUS_KEY]; else n[CLS_STATUS_KEY] = [s];
      return n;
    });
  const [sort, setSort] = useState<SortState | null>(null);
  // The detail drawer opens on a row click only (Pedro 2026-10-26: "We don't need the side pane
  // open at first"); a link from elsewhere fills Search instead (`?q=`, utils/securityLink).
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [openCol, setOpenCol] = useState<string | null>(null);

  // column layout, persisted locally
  const [hidden, setHidden] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem('securities.hiddenCols') || '[]')); }
    catch { return new Set(); }
  });
  useEffect(() => { localStorage.setItem('securities.hiddenCols', JSON.stringify([...hidden])); }, [hidden]);
  const [freezeKey, setFreezeKey] = useState<string>(() => localStorage.getItem('securities.freezeKey') ?? 'description');
  useEffect(() => { localStorage.setItem('securities.freezeKey', freezeKey); }, [freezeKey]);
  const [levelKeys, setLevelKeys] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('securities.groupLevels') || '[]') as string[]; }
    catch { return []; }
  });
  useEffect(() => { try { localStorage.setItem('securities.groupLevels', JSON.stringify(levelKeys)); } catch { /* private window */ } }, [levelKeys]);
  const [folded, setFolded] = useState<Set<string>>(new Set());

  // edit mode (Inc 1)
  const [editing, setEditing] = useState(false);
  const [drafts, setDrafts] = useState<Drafts>({});
  const [clsDrafts, setClsDrafts] = useState<ClsDrafts>({});
  // "Undo last change" (TRADE-1): the staged state before each change, newest last.
  const [undoStack, setUndoStack] = useState<{ drafts: Drafts; clsDrafts: ClsDrafts }[]>([]);
  const [active, setActive] = useState<{ id: string; key: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SecurityRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Model Portfolios M1: the un-approve check before Save (MQ5) — declared here, above the early
  // returns (rules of hooks). M1-SHOW: the list is shown on screen first, the download under it.
  const [usageAsk, setUsageAsk] = useState<{ items: SecurityUsage[]; resolve: (go: boolean) => void } | null>(null);
  const [usageShown, setUsageShown] = useState(false);

  const dirtyIds = useMemo(() => new Set([...Object.keys(drafts), ...Object.keys(clsDrafts)]), [drafts, clsDrafts]);
  const dirtyCount = dirtyIds.size;
  const dirty = editing && dirtyCount > 0;
  // The leave-page guard also offers "Save & leave" (Pedro 2026-10-10, same as the
  // Persons list's staged checkboxes): it runs handleSave, kept current in a ref.
  const saveRef = useRef<() => Promise<boolean>>(async () => true);
  useEffect(
    () => registerDirty('securities-grid-editor', dirty, () => saveRef.current()),
    [dirty, registerDirty],
  );

  const listQ = useQuery({
    queryKey: ['securities', 'list', 'all'],
    queryFn: () => securitiesApi.list({}),
    enabled: canRead,
  });
  const dimsQ = useQuery({
    queryKey: ['classification', 'dimensions'],
    queryFn: () => classificationApi.dimensions(),
    enabled: canRead,
  });
  // The firm-wide active view governs which lens columns show, and their order (mig 0094).
  const activeViewQ = useQuery({
    queryKey: ['classification', 'active-view'],
    queryFn: () => classificationApi.activeScheme(),
    enabled: canRead,
  });
  const clsValuesQ = useQuery({
    queryKey: ['classification', 'values-all'],
    queryFn: async () => {
      const dims = await classificationApi.dimensions();
      const pairs = await Promise.all(dims.map(async (d) => [d.code, await classificationApi.values(d.code)] as const));
      return Object.fromEntries(pairs) as Record<string, ClassificationValue[]>;
    },
    enabled: canRead,
  });
  const dimByCode = useMemo(
    () => Object.fromEntries((dimsQ.data ?? []).map((d) => [d.code, d])),
    [dimsQ.data],
  );
  // per-dimension helpers: a value's roll-up path, the ancestor at a given tier, and
  // the pickable children AT a tier under a parent (parent-scoped per-tier pickers).
  const clsIndex = useMemo(() => {
    const out: Record<string, {
      pathOf: (id: string) => string[];
      /** The taxonomy sort orders along a value's path, top tier first (for grouping). */
      ordersOf: (id: string) => number[];
      ancestorAtTier: (valueId: string, tier: number) => string | null;
      childrenAt: (tier: number, parentId: string | null) => { value: string; label: string }[];
    }> = {};
    for (const [code, vals] of Object.entries(clsValuesQ.data ?? {})) {
      const byId: Record<string, ClassificationValue> = Object.fromEntries(vals.map((v) => [v.id, v]));
      const pathOf = (id: string) => {
        const p: string[] = []; let cur: ClassificationValue | undefined = byId[id]; const seen = new Set<string>();
        while (cur && !seen.has(cur.id)) { seen.add(cur.id); p.push(cur.label); cur = cur.parent_id ? byId[cur.parent_id] : undefined; }
        return p.reverse();
      };
      const ordersOf = (id: string) => {
        const p: number[] = []; let cur: ClassificationValue | undefined = byId[id]; const seen = new Set<string>();
        while (cur && !seen.has(cur.id)) { seen.add(cur.id); p.push(cur.sort_order); cur = cur.parent_id ? byId[cur.parent_id] : undefined; }
        return p.reverse();
      };
      const ancestorAtTier = (valueId: string, tier: number) => {
        let cur: ClassificationValue | undefined = byId[valueId]; const seen = new Set<string>();
        while (cur && !seen.has(cur.id)) {
          if (cur.tier_level === tier) return cur.id;
          seen.add(cur.id); cur = cur.parent_id ? byId[cur.parent_id] : undefined;
        }
        return null;
      };
      const childrenAt = (tier: number, parentId: string | null) =>
        vals
          .filter((v) => v.tier_level === tier && (tier === 1 ? !v.parent_id : v.parent_id === parentId))
          .sort((a, b) => (a.sort_order - b.sort_order) || a.label.localeCompare(b.label))
          .map((v) => ({ value: v.id, label: v.label }));
      out[code] = { pathOf, ordersOf, ancestorAtTier, childrenAt };
    }
    return out;
  }, [clsValuesQ.data]);

  const allRows = useMemo(() => listQ.data?.items ?? [], [listQ.data]);
  // HP (Pedro 2026-10-18): securities with no classification at all — e.g. appended from a
  // portfolio upload — are easy to find and classify. Read from the SAVED rows, so a row
  // being classified in Edit mode stays in view until Save.
  const nonClassifiedIds = useMemo(
    () => new Set(allRows.filter((r) => r.classifications.length === 0).map((r) => r.id)),
    [allRows],
  );
  // Partly classified (Pedro 2026-10-18): a security with some classification where, on a
  // lens of the active view that applies to it, the reading is missing or stops above the
  // lens's deepest tier (e.g. Asset Class "Equities" with no sub-class). A scoped lens
  // applies when the security's asset class is in its scope; without an asset class it is
  // not judged. Saved rows only, like the non-classified set. Value: what is missing.
  const partialReasons = useMemo(() => {
    const out = new Map<string, string[]>();
    const av = activeViewQ.data;
    const lenses = (av ? av.dimensions.map((d) => dimByCode[d.code]) : (dimsQ.data ?? []))
      .filter((dm): dm is NonNullable<typeof dm> => !!dm);
    for (const r of allRows) {
      if (r.classifications.length === 0) continue;
      const assetPath = r.classifications.find((c) => c.dimension_code === 'asset_class')?.path ?? [];
      const reasons: string[] = [];
      for (const dm of lenses) {
        const applies = dm.scope !== 'scoped' || dm.applies_to.some((l) => assetPath.includes(l));
        if (!applies) continue;
        const reading = r.classifications.find((c) => c.dimension_code === dm.code);
        const deepest = Math.max(0, ...dm.tiers.map((t) => t.tier_level));
        if (!reading) reasons.push(`${dm.name}: missing`);
        else if (reading.tier_level < deepest) {
          const next = dm.tiers.find((t) => t.tier_level === reading.tier_level + 1)?.name ?? 'next tier';
          reasons.push(`${dm.name}: no ${next}`);
        }
      }
      if (reasons.length) out.set(r.id, reasons);
    }
    return out;
  }, [allRows, activeViewQ.data, dimsQ.data, dimByCode]);

  // per-tier classification columns, ordered by the firm-wide ACTIVE VIEW (mig 0094).
  // Falls back to all dimensions in taxonomy sort_order until the active view loads —
  // behaviour-preserving on the seeded Default view (includes_all_lenses).
  const clsCols = useMemo<Col[]>(() => {
    const allDims = dimsQ.data ?? [];
    const av = activeViewQ.data;
    const ordered = av
      ? av.dimensions.map((d) => dimByCode[d.code]).filter((dm): dm is NonNullable<typeof dm> => !!dm)
      : allDims;
    return ordered.flatMap((dm) =>
      dm.tiers.map((t) => ({
        key: `cls:${dm.code}:${t.tier_level}`, label: t.name, width: 160, ft: 'lens' as Ft,
        code: dm.code, tierLevel: t.tier_level, dimName: dm.name, group: dm.name,
      })));
  }, [dimsQ.data, activeViewQ.data, dimByCode]);
  const clsStatus = useMemo(() => (row: SecurityRow) =>
    (nonClassifiedIds.has(row.id) ? NON : partialReasons.has(row.id) ? PARTLY : FULLY),
  [nonClassifiedIds, partialReasons]);
  const fullyCount = allRows.length - nonClassifiedIds.size - partialReasons.size;
  const COLS = useMemo<Col[]>(() => [
    ...ATTR_HEAD,
    // Sorts least to most complete (Pedro 2026-10-19): Asc = Non → Partly → Fully, Desc the reverse.
    { key: CLS_STATUS_KEY, label: 'Classification', width: 150, ft: 'enum', group: 'Classification status', derived: clsStatus,
      sortValue: (r: SecurityRow) => [NON, PARTLY, FULLY].indexOf(clsStatus(r)) },
    ...clsCols, ...ATTR_TAIL,
  ], [clsCols, clsStatus]);
  // The user's column order (dragged headings or the Columns list; HP-V2, shared with Portfolios).
  const colOrder = useColumnOrder('securities.columnOrder', COLS.map((c) => c.key));
  const orderedCols = useMemo(() => ordered(COLS, colOrder.order), [COLS, colOrder.order]);
  // Widths dragged at a heading's right edge (HP-V4). The first column also carries the BM
  // badge, so until the user sets its width it widens by it when in use (Pedro 2026-10-19:
  // "everything fits and is visible"); the classification badges have their own column (HP-V5).
  const colWidths = useColumnWidths('securities.columnWidths');
  const badgeExtra = allRows.some((r) => r.is_benchmark) ? 32 : 0;
  // As in View portfolio, a classification used as a Group-by level is not shown in line (the
  // group rows say it) — except in Edit mode, where its cells are where it is edited.
  const groupedKeys = useMemo(() => new Set(editing ? [] : levelKeys), [editing, levelKeys]);
  const unavailable = Object.fromEntries([...groupedKeys].map((k) => [`cls:${k}`, 'used for grouping']));
  const visibleCols = useMemo(() => orderedCols
    .filter((c) => !hidden.has(c.key) && !groupedKeys.has(c.key.replace(/^cls:/, '')))
    .map((c, i) => ({ ...c, width: colWidths.widthOf(c.key, c.width + (i === 0 ? badgeExtra : 0)) })),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [orderedCols, hidden, colWidths.widthOf, badgeExtra, groupedKeys]);
  const headingDrag = useHeadingDrag(colOrder.place);
  const leftOffsets = useMemo(
    () => visibleCols.reduce<number[]>((acc, _c, i) => { acc[i] = i === 0 ? 0 : acc[i - 1] + visibleCols[i - 1].width; return acc; }, []),
    [visibleCols],
  );
  const totalW = useMemo(() => visibleCols.reduce((s, c) => s + c.width, 0), [visibleCols]);
  const freezeVisIdx = useMemo(() => visibleCols.findIndex((c) => c.key === freezeKey), [visibleCols, freezeKey]);


  const options = useMemo(() => {
    const m: Record<string, Set<string>> = {};
    COLS.forEach((c) => { if (c.ft === 'enum' || c.ft === 'lens') m[c.key] = new Set(); });
    for (const row of allRows) {
      for (const c of COLS) {
        if (c.ft === 'enum') { const v = cellText(c, row); if (v && v !== '—') m[c.key].add(v); }
        else if (c.ft === 'lens') { const v = tierValue(c, row); if (v) m[c.key].add(v); }
      }
    }
    const out: Record<string, string[]> = {};
    Object.keys(m).forEach((k) => { out[k] = [...m[k]].sort(); });
    return out;
  }, [allRows, COLS]);

  const merged = useMemo(() => {
    if (dirtyCount === 0) return allRows;
    return allRows.map((r) => {
      let row = drafts[r.id] ? { ...r, ...drafts[r.id] } : r;
      const cd = clsDrafts[r.id];
      if (cd) {
        const others = row.classifications.filter((c) => !(c.dimension_code in cd));
        const added = Object.entries(cd)
          .filter(([, vid]) => vid != null)
          .map(([code, vid]) => {
            const path = clsIndex[code]?.pathOf(vid as string) ?? [];
            return {
              dimension_code: code, dimension_name: dimByCode[code]?.name ?? code,
              value_id: vid as string, value_label: path[path.length - 1] ?? '',
              tier_level: path.length, path,
            };
          });
        const classifications = [...others, ...added];
        const asset_class = classifications.find((c) => c.dimension_code === 'asset_class')?.path[0] ?? null;
        row = { ...row, classifications, asset_class };
      }
      return row;
    });
  }, [allRows, drafts, clsDrafts, dirtyCount, clsIndex, dimByCode]);

  const displayed = useMemo(() => {
    const term = search.trim().toLowerCase();
    const match = (row: SecurityRow) => {
      if (term) {
        const hay = `${row.isin} ${row.bb_ticker ?? ''} ${row.description}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      for (const c of COLS) {
        const f = filters[c.key];
        if (!filterActive(c, f)) continue;
        if (c.ft === 'text') { if (!cellText(c, row).toLowerCase().includes((f as string).toLowerCase())) return false; }
        else if (c.ft === 'enum') { if (!(f as string[]).includes(cellText(c, row))) return false; }
        else if (c.ft === 'lens') { if (!(f as string[]).includes(tierValue(c, row))) return false; }
        else if (c.ft === 'date') { const { from, to } = f as { from: string; to: string }; const v = (row[c.field!] as string | null) ?? ''; if (from && (!v || v < from)) return false; if (to && (!v || v > to)) return false; }
        else if (c.ft === 'num') { const { min, max } = f as { min: string; max: string }; const v = row[c.field!] as number | null; if (min !== '' && (v == null || v < Number(min))) return false; if (max !== '' && (v == null || v > Number(max))) return false; }
      }
      return true;
    };
    const out = merged.filter(match);
    if (sort) {
      const c = COLS.find((x) => x.key === sort.key);
      if (c) {
        const val = (r: SecurityRow): string | number => {
          if (c.sortValue) return c.sortValue(r);
          if (c.ft === 'num') return (r[c.field!] as number | null) ?? -Infinity;
          if (c.ft === 'date') return (r[c.field!] as string | null) ?? '';
          if (c.ft === 'lens') return tierValue(c, r);
          return cellText(c, r);
        };
        out.sort((a, b) => {
          const av = val(a), bv = val(b);
          const r = av < bv ? -1 : av > bv ? 1 : 0;
          return sort.dir === 'desc' ? -r : r;
        });
      }
    }
    return out;
  }, [merged, filters, sort, search, COLS]);

  // Group by (HP-V4, Pedro 2026-10-19: "allow for Securities to be grouped in the same way as
  // Portfolios"): the same ordered, foldable levels and rules (utils/portfolioGrouping), over
  // the lenses of the active view. Starts ungrouped; the levels are remembered on this computer.
  const groupLenses = useMemo<GroupLens[]>(() => {
    const av = activeViewQ.data;
    const dims = av ? av.dimensions.map((d) => dimByCode[d.code]).filter((dm): dm is NonNullable<typeof dm> => !!dm)
      : (dimsQ.data ?? []);
    return dims.map((dm) => ({ code: dm.code, name: dm.name,
      tiers: dm.tiers.map((t) => ({ level: t.tier_level, name: t.name })) }));
  }, [activeViewQ.data, dimsQ.data, dimByCode]);
  const levels = validLevels(levelKeys, groupLenses);
  const grouped = useMemo(() => groupRows<SecurityRow>(displayed, levels, groupLenses, {
    path: (r, code) => r.classifications.find((c) => c.dimension_code === code)?.path,
    orders: (r, code) => {
      const vid = r.classifications.find((c) => c.dimension_code === code)?.value_id;
      return vid ? (clsIndex[code]?.ordersOf(vid) ?? []) : [];
    },
    special: (r) => (r.classifications.length === 0 ? 'non' : null),
    value: () => 0,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [displayed, levelKeys.join('|'), groupLenses, clsIndex]);
  type GridRow = { kind: 'group'; node: GroupNode<SecurityRow> } | { kind: 'row'; row: SecurityRow; ri: number; depth: number };
  const gridRows: GridRow[] = [];
  if (levels.length === 0) {
    displayed.forEach((row, ri) => gridRows.push({ kind: 'row', row, ri, depth: 0 }));
  } else {
    const walk = (nodes: GroupNode<SecurityRow>[]) => {
      for (const node of nodes) {
        gridRows.push({ kind: 'group', node });
        if (folded.has(node.id)) continue;
        if (node.children.length) walk(node.children);
        else node.lines.forEach((row, ri) => gridRows.push({ kind: 'row', row, ri, depth: node.depth + 1 }));
      }
    };
    walk(grouped.groups);
  }
  const groupIds = levels.length ? allGroupIds(grouped.groups) : [];
  const toggleFold = (id: string) => setFolded((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  if (!user) return null;
  if (!canRead) return <AccessDeniedAlert />;

  const applySort = (colKey: string, dir: 'asc' | 'desc') => {
    setSort((prev) => (prev && prev.key === colKey && prev.dir === dir ? null : { key: colKey, dir }));
    setOpenCol(null);
  };
  const applyFilter = (colKey: string, draft: FilterValue) => {
    const col = COLS.find((c) => c.key === colKey)!;
    setFilters((prev) => {
      const n = { ...prev };
      if (filterActive(col, draft)) n[colKey] = draft; else delete n[colKey];
      return n;
    });
    setOpenCol(null);
  };
  const clearCol = (colKey: string) => {
    setFilters((prev) => { const n = { ...prev }; delete n[colKey]; return n; });
    setSort((prev) => (prev?.key === colKey ? null : prev));
    setOpenCol(null);
  };
  const toggleCol = (key: string) => setHidden((prev) => {
    const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n;
  });

  const handleExport = async () => {
    setExporting(true);
    try {
      await securitiesApi.exportXlsx(displayed.map((r) => r.id));
    } catch (e) {
      notifications.show({ color: 'red', title: 'Export failed', message: errMsg(e) });
    } finally {
      setExporting(false);
    }
  };

  // ── edit-mode helpers ───────────────────────────────────────────────────────
  const commitCell = (id: string, colKey: string, raw: string) => {
    const col = COLS.find((c) => c.key === colKey)!;
    const kind = editKind(col)!;
    const field = col.field as keyof SecurityRow;
    let val: string | number | boolean | null;
    if (kind === 'num') val = raw.trim() === '' ? null : Number(raw);
    else if (kind === 'bool') val = raw === '' ? null : raw === 'true';
    else val = raw.trim() === '' ? null : raw;
    setActive(null);
    if (kind === 'num' && val != null && Number.isNaN(val)) return;
    if (colKey === 'description' && (val == null || val === '')) return;
    const orig = (allRows.find((r) => r.id === id)?.[field] ?? null) as unknown;
    stage(stageField(drafts, id, field as string, val, orig), clsDrafts);
  };

  const commitCls = (id: string, dimCode: string, vid: string | null) => {
    setActive(null);
    const orig = allRows.find((r) => r.id === id)?.classifications.find((c) => c.dimension_code === dimCode)?.value_id ?? null;
    const had = Object.prototype.hasOwnProperty.call(clsDrafts[id] ?? {}, dimCode);
    const same = (vid ?? null) === (orig ?? null);
    if (same ? !had : had && clsDrafts[id][dimCode] === (vid ?? null)) return;
    const cur = { ...(clsDrafts[id] ?? {}) };
    if (same) delete cur[dimCode]; else cur[dimCode] = vid ?? null;
    const n = { ...clsDrafts };
    if (Object.keys(cur).length === 0) delete n[id]; else n[id] = cur;
    stage(drafts, n);
  };

  // Every staged change goes through here, so "Undo last change" can step back one at a time.
  function stage(nd: Drafts, ncd: ClsDrafts) {
    if (nd === drafts && ncd === clsDrafts) return;
    setUndoStack((s) => [...s, { drafts, clsDrafts }]);
    setDrafts(nd); setClsDrafts(ncd);
  }
  const undoLast = () => {
    const last = undoStack[undoStack.length - 1];
    if (!last) return;
    setUndoStack(undoStack.slice(0, -1));
    setDrafts(last.drafts); setClsDrafts(last.clsDrafts); setActive(null);
  };
  const savedById = new Map(allRows.map((r) => [r.id, r]));
  const toggleApproved = (id: string) => {
    const row = merged.find((r) => r.id === id);
    if (!row) return;
    stage(stageField(drafts, id, APPROVED_KEY, !row.authorized, savedById.get(id)?.authorized ?? null), clsDrafts);
  };
  // "Approve all shown" / "Unapprove all shown": every row the search and filters show.
  const setApprovedShown = (v: boolean) =>
    stage(displayed.reduce((acc, r) => stageField(acc, r.id, APPROVED_KEY, v, savedById.get(r.id)?.authorized ?? null), drafts), clsDrafts);
  const shownApproved = displayed.filter((r) => r.authorized).length;

  const discardEdits = () => { setDrafts({}); setClsDrafts({}); setUndoStack([]); setActive(null); setEditing(false); };
  const leaveEdit = async () => {
    if (dirty) { const ok = await confirmDiscardAsync(); if (!ok) return; }
    discardEdits();
  };

  // Model Portfolios M1 (Pedro 2026-10-26, MQ5): un-approving a security that a bucket and / or a
  // portfolio (latest positions) holds is flagged before Save, with a downloadable list; the
  // user may still save (the bucket shows it red and stays usable).
  const confirmUnapprove = async (): Promise<boolean> => {
    const ids = Object.keys(drafts).filter((id) => drafts[id].authorized === false && savedById.get(id)?.authorized === true);
    if (ids.length === 0) return true;
    let items: SecurityUsage[];
    try {
      items = (await securityUsageApi.of(ids)).items;
    } catch (e) {
      notifications.show({ color: 'red', title: 'Could not check where these securities are held', message: errMsg(e) });
      return false;
    }
    if (items.length === 0) return true;
    return new Promise<boolean>((resolve) => setUsageAsk({ items, resolve }));
  };
  const answerUsage = (go: boolean) => { usageAsk?.resolve(go); setUsageAsk(null); setUsageShown(false); };

  // Resolves true when everything saved (the leave-page guard's Save & leave waits on it).
  const handleSave = async (): Promise<boolean> => {
    const attrIds = Object.keys(drafts);
    const clsIds = Object.keys(clsDrafts);
    if (attrIds.length === 0 && clsIds.length === 0) { setEditing(false); return true; }
    if (!(await confirmUnapprove())) return false;
    const n = dirtyIds.size;
    setSaving(true);
    try {
      await Promise.all([
        ...attrIds.map((id) => securitiesApi.update(id, drafts[id] as SecurityUpdatePayload)),
        ...clsIds.map((id) => securitiesApi.setClassifications(id, clsDrafts[id])),
      ]);
      await qc.invalidateQueries({ queryKey: ['securities'] });
      notifications.show({ color: 'green', title: 'Saved', message: `Updated ${n} securit${n === 1 ? 'y' : 'ies'}.` });
      setDrafts({}); setClsDrafts({}); setUndoStack([]); setActive(null); setEditing(false);
      return true;
    } catch (e) {
      notifications.show({ color: 'red', title: 'Save failed', message: errMsg(e) });
      return false;
    } finally {
      setSaving(false);
    }
  };
  // A plain assignment, not a hook: this point is past the page's early returns.
  saveRef.current = handleSave;

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await securitiesApi.remove(deleteTarget.id);
      await qc.invalidateQueries({ queryKey: ['securities'] });
      const gone = deleteTarget.id;
      const without = <T extends object>(m: Record<string, T>) => { const n = { ...m }; delete n[gone]; return n; };
      setDrafts((prev) => without(prev));
      setClsDrafts((prev) => without(prev));
      // Undo must never bring back a change to a deleted security.
      setUndoStack((s) => s.map((e) => ({ drafts: without(e.drafts), clsDrafts: without(e.clsDrafts) })));
      notifications.show({ color: 'green', title: 'Deleted', message: `${deleteTarget.description} was removed.` });
      setDeleteTarget(null);
    } catch (e) {
      notifications.show({ color: 'red', title: 'Delete failed', message: errMsg(e) });
    } finally {
      setDeleting(false);
    }
  };

  const headBg = 'var(--mantine-color-gray-1)';
  const zebra = (i: number) => (i % 2 ? 'var(--mantine-color-gray-0)' : 'var(--mantine-color-body)');
  const cellStyle = (vi: number, bg: string, isHead: boolean): React.CSSProperties => {
    const col = visibleCols[vi];
    const frozen = vi <= freezeVisIdx;
    return {
      width: col.width, minWidth: col.width, maxWidth: col.width,
      padding: '6px 10px', boxSizing: 'border-box', background: bg,
      borderBottom: '1px solid var(--mantine-color-gray-3)',
      borderRight: vi === freezeVisIdx ? '2px solid var(--mantine-color-gray-4)' : '1px solid var(--mantine-color-gray-2)',
      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      fontSize: 13, textAlign: !isHead && col.num ? 'right' : 'left',
      position: (isHead || frozen) ? 'sticky' : undefined,
      top: isHead ? 0 : undefined,
      left: frozen ? leftOffsets[vi] : undefined,
      zIndex: isHead && frozen ? 4 : isHead ? 3 : frozen ? 2 : undefined,
    };
  };

  const activeCount = COLS.filter((c) => filterActive(c, filters[c.key])).length + (sort ? 1 : 0);
  // The Classification cell: yellow while something is missing, green when complete (Pedro).
  const statusBadge = (row: SecurityRow) => {
    const s = clsStatus(row);
    const tip = s === NON ? 'No classification yet: it shows under Non-Classified Securities in Portfolios'
      : s === PARTLY ? `Partly classified — ${partialReasons.get(row.id)!.join('; ')}`
      : 'Classified on every lens of the active view that applies to it, down to its deepest tier';
    return (
      <Tooltip withArrow multiline w={320} label={tip}>
        <Badge size="xs" color={s === FULLY ? 'green' : 'yellow'} variant={s === PARTLY ? 'outline' : 'light'}
          style={{ flex: '0 0 auto' }}>{s}</Badge>
      </Tooltip>
    );
  };

  return (
    <Container size="xl" fluid>
      <Stack>
        <div>
          {isAdmin
            ? <BackToCompliance to="/admin" label="Back to Admin" />
            : <BackToCompliance to="/" label="Back to Welcome" />}
          <Title order={2}>Securities</Title>
          <Text size="sm" c="dimmed">
            The securities master — attributes + per-tier classifications, frozen leading columns,
            per-column sort &amp; filter, hide/show columns, export to Excel.
          </Text>
        </div>

        <Grid align="flex-end">
          <Grid.Col span={{ base: 12, sm: 5 }}>
            <TextInput label="Search" placeholder="ISIN, BB ticker or name"
              leftSection={<IconSearch size={14} />}
              value={search} onChange={(e) => setSearch(e.currentTarget.value)} />
          </Grid.Col>
          <Grid.Col span={{ base: 8, sm: 3 }}>
            <Select label="Freeze columns through" allowDeselect={false}
              data={[{ value: '__none__', label: 'None' }, ...visibleCols.map((c) => ({ value: c.key, label: c.label }))]}
              value={freezeKey} onChange={(v) => setFreezeKey(v ?? '__none__')} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Group gap="xs" align="flex-end" wrap="nowrap">
              <MultiSelect label="Group by (in order)" clearable style={{ flex: 1 }}
                placeholder={levels.length ? undefined : 'No grouping'}
                data={levelOptions(groupLenses)} value={levels.map(levelKey)}
                onChange={(ks) => { setLevelKeys(ks); setFolded(new Set()); }} />
              {groupIds.length > 0 && (
                <Button variant="subtle" onClick={() => setFolded(folded.size ? new Set() : new Set(groupIds))}>
                  {folded.size ? 'Unfold all' : 'Fold all'}
                </Button>
              )}
            </Group>
          </Grid.Col>
        </Grid>

        <Divider />

        <Group justify="space-between">
          <Group gap="sm">
            <Text size="sm" c="dimmed">
              {listQ.data ? `${displayed.length} of ${allRows.length} securities` : listQ.isLoading ? 'Loading…' : ''}
            </Text>
            <Tooltip withArrow label={nonClassifiedIds.size === 0
              ? 'Every security has a classification'
              : 'Show only the securities with no classification yet (e.g. added from a portfolio upload)'}>
              <span>
                <Button size="xs" color="yellow" variant={onlyStatus(NON) ? 'filled' : 'light'}
                  disabled={nonClassifiedIds.size === 0 && !onlyStatus(NON)}
                  onClick={() => toggleStatusShortcut(NON)}>
                  Non-classified only ({nonClassifiedIds.size})
                </Button>
              </span>
            </Tooltip>
            <Tooltip withArrow label={partialReasons.size === 0
              ? 'No security is partly classified'
              : 'Show only the securities with some classification but a lens missing or stopping above its deepest tier (hover a row\'s badge for what is missing)'}>
              <span>
                <Button size="xs" color="yellow" variant={onlyStatus(PARTLY) ? 'filled' : 'light'}
                  disabled={partialReasons.size === 0 && !onlyStatus(PARTLY)}
                  onClick={() => toggleStatusShortcut(PARTLY)}>
                  Partly classified only ({partialReasons.size})
                </Button>
              </span>
            </Tooltip>
            <Tooltip withArrow label={fullyCount === 0
              ? 'No security is fully classified yet'
              : 'Show only the securities classified on every lens of the active view that applies to them, down to its deepest tier'}>
              <span>
                <Button size="xs" color="green" variant={onlyStatus(FULLY) ? 'filled' : 'light'}
                  disabled={fullyCount === 0 && !onlyStatus(FULLY)}
                  onClick={() => toggleStatusShortcut(FULLY)}>
                  Fully classified only ({fullyCount})
                </Button>
              </span>
            </Tooltip>
            {activeCount > 0 && (
              <Anchor size="xs" onClick={() => { setFilters({}); setSort(null); }}>Clear all filters</Anchor>
            )}
            {editing && (
              <Badge color="orange" variant="light">
                {dirtyCount === 0 ? 'Editing — click a cell' : `${dirtyCount} unsaved`}
              </Badge>
            )}
          </Group>
          <Group gap="sm">
            <ColumnsPicker columns={orderedCols} hidden={hidden} onToggle={toggleCol} onShowAll={() => setHidden(new Set())}
              onPlace={colOrder.place} onResetOrder={() => { colOrder.reset(); colWidths.reset(); }}
              customOrder={colOrder.custom || colWidths.custom} unavailable={unavailable} />
            {canManage && !editing && (
              <>
                <Button variant="light" leftSection={<IconUpload size={16} />} onClick={() => setImportOpen(true)}>
                  Upload
                </Button>
                <Button variant="light" leftSection={<IconPlus size={16} />} onClick={() => setCreateOpen(true)}>
                  New security
                </Button>
                <Button variant="light" leftSection={<IconEdit size={16} />} onClick={() => setEditing(true)}>
                  Edit securities
                </Button>
              </>
            )}
            {canManage && editing && (
              <>
                <Tooltip withArrow multiline w={300}
                  label={`Ticks Approved on the ${displayed.length} securities shown by the search and filters. Pending until you Save.`}>
                  <span>
                    <Button variant="light" color="green" disabled={displayed.length === 0 || shownApproved === displayed.length}
                      onClick={() => setApprovedShown(true)}>
                      Approve all shown ({displayed.length})
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip withArrow multiline w={300}
                  label={`Clears Approved on the ${displayed.length} securities shown by the search and filters. Pending until you Save.`}>
                  <span>
                    <Button variant="light" color="gray" disabled={shownApproved === 0}
                      onClick={() => setApprovedShown(false)}>
                      Unapprove all shown ({displayed.length})
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip withArrow label={undoStack.length ? 'Undo your last change (one step at a time)' : 'Nothing to undo'}>
                  <span>
                    <Button variant="default" leftSection={<IconArrowBackUp size={16} />}
                      disabled={undoStack.length === 0} onClick={undoLast}>
                      Undo last change
                    </Button>
                  </span>
                </Tooltip>
                <Button variant="default" onClick={leaveEdit}>Discard</Button>
                <Button onClick={handleSave} loading={saving} disabled={dirtyCount === 0}>
                  Save changes
                </Button>
              </>
            )}
            {/* Data exports are Admin-only (C-335); hidden for the P45 readers (D7). */}
            {isAdmin && (
              <Button variant="light" leftSection={<IconDownload size={16} />}
                onClick={handleExport} loading={exporting} disabled={displayed.length === 0}>
                Export to Excel
              </Button>
            )}
          </Group>
        </Group>

        {editing && (
          <Text size="xs" c="dimmed">
            Click a cell to edit it — classification cells open a picker (any tier column of a
            dimension sets its one value; parents fill in automatically). Enter or click away to keep
            the change, Esc to cancel. Approved is a tick box: click it, or use Approve / Unapprove all
            shown for every row the search and filters show. The frozen identifiers (ISIN, currency,
            BB Ticker, FIGI) can’t be edited here. Changes stay pending until you Save; Undo last
            change steps back one change at a time.
          </Text>
        )}

        {listQ.isError && <Alert color="red">{errMsg(listQ.error)}</Alert>}
        {listQ.isLoading && <Loader />}
        {listQ.data && (
          <div style={{ overflow: 'auto', height: 'calc(100vh - 430px)', minHeight: 260, border: '1px solid var(--mantine-color-gray-3)', borderRadius: 6 }}>
            <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: totalW, tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  {visibleCols.map((c, vi) => {
                    const active = filterActive(c, filters[c.key]) || sort?.key === c.key;
                    const bg = active ? 'var(--mantine-color-azul-1)' : headBg;
                    const tip = c.ft === 'lens' && c.dimName ? `${c.dimName} — ${c.label}` : c.label;
                    return (
                      <th key={c.key} {...headingDrag.props(c.key)} title="Drag to move this column"
                        style={{ ...cellStyle(vi, bg, true), ...headingDrag.marker(c.key) }}>
                        <Popover width={250} position="bottom-end" withArrow shadow="md" trapFocus
                          keepMounted={false}
                          opened={openCol === c.key}
                          onChange={(o) => setOpenCol(o ? c.key : null)}>
                          <Popover.Target>
                            <Group gap={4} wrap="nowrap" justify="space-between"
                              role="button" aria-label={`Sort or filter ${c.label}`}
                              onClick={() => setOpenCol(openCol === c.key ? null : c.key)}
                              style={{ cursor: 'pointer' }}>
                              <span title={tip} style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.label}</span>
                              <span style={{ color: active ? 'var(--mantine-color-azul-7)' : 'var(--mantine-color-gray-5)', display: 'inline-flex', flex: '0 0 auto' }}>
                                {sort?.key === c.key
                                  ? (sort.dir === 'asc' ? <IconSortAscending size={14} /> : <IconSortDescending size={14} />)
                                  : <IconFilter size={14} />}
                              </span>
                            </Group>
                          </Popover.Target>
                          <Popover.Dropdown>
                            <ColumnMenu col={c} options={options[c.key] ?? []}
                              initialFilter={filters[c.key]} sort={sort}
                              onSort={(dir) => applySort(c.key, dir)}
                              onApply={(draft) => applyFilter(c.key, draft)}
                              onClear={() => clearCol(c.key)} />
                          </Popover.Dropdown>
                        </Popover>
                        <ColumnResizeHandle width={c.width} onResize={(w) => colWidths.setWidth(c.key, w)} />
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {gridRows.map((gr) => {
                  if (gr.kind === 'group') {
                    const node = gr.node;
                    const isFolded = folded.has(node.id);
                    return (
                      <tr key={`g:${node.id}`} onClick={() => toggleFold(node.id)} style={{ cursor: 'pointer' }}
                        title={isFolded ? 'Click to unfold' : 'Click to fold'}>
                        {visibleCols.map((c, vi) => (
                          <td key={c.key} style={{ ...cellStyle(vi, groupRowBg(node.depth), false), fontWeight: 600 }}>
                            {vi === 0 && (
                              <span style={{ paddingLeft: node.depth * 16, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                {isFolded ? <IconChevronRight size={14} /> : <IconChevronDown size={14} />}
                                {node.label} <Text span size="xs" c="dimmed" fw={400}>({node.lineCount})</Text>
                              </span>
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  }
                  const { row, ri, depth } = gr;
                  const bg = zebra(ri);
                  const rowDraft = drafts[row.id];
                  return (
                    <tr key={row.id} style={{ cursor: editing ? 'default' : 'pointer' }}
                      onClick={editing ? undefined : () => setSelectedId(row.id)}>
                      {visibleCols.map((c, vi) => {
                        const kind = editKind(c);
                        const isLens = c.ft === 'lens';
                        const dim = isLens ? dimByCode[c.code ?? ''] : undefined;
                        const idx = isLens ? clsIndex[c.code ?? ''] : undefined;
                        const tier = c.tierLevel ?? 1;
                        const readingId = isLens
                          ? (row.classifications.find((cc) => cc.dimension_code === c.code)?.value_id ?? null)
                          : null;
                        // deeper tiers are scoped to the value chosen one level up
                        const parentTierId = isLens && idx && tier > 1 && readingId
                          ? idx.ancestorAtTier(readingId, tier - 1) : null;
                        const inScope = !!dim &&
                          (dim.scope !== 'scoped' || (!!row.asset_class && dim.applies_to.includes(row.asset_class)));
                        const parentReady = tier === 1 || parentTierId != null;
                        const clsEditable = editing && isLens && !!idx && inScope && parentReady;
                        const editable = editing && (kind != null || clsEditable);
                        const isActive = editing && active?.id === row.id && active?.key === c.key;
                        const dirtyCell = isLens
                          ? (!!clsDrafts[row.id] && Object.prototype.hasOwnProperty.call(clsDrafts[row.id], c.code ?? ''))
                          : (!!rowDraft && c.field != null && Object.prototype.hasOwnProperty.call(rowDraft, c.field));
                        const cellBg = dirtyCell ? 'var(--mantine-color-orange-1)' : bg;
                        const st: React.CSSProperties = {
                          ...cellStyle(vi, cellBg, false),
                          ...(editable ? { cursor: isLens ? 'pointer' : 'text' } : {}),
                          ...(isActive ? { overflow: 'visible' } : {}),
                          ...(editing && !editable && vi !== 0 ? { color: 'var(--mantine-color-gray-6)' } : {}),
                        };
                        const initial = (() => {
                          const v = row[c.field!];
                          if (kind === 'bool') return v == null ? '' : v ? 'true' : 'false';
                          if (v == null) return '';
                          return String(v);
                        })();
                        if (c.key === APPROVED_KEY) {
                          // A tick box: in Read mode it only shows (greyed, §3h); in Edit mode a click
                          // anywhere in the cell stages the change (pending until Save, §3g).
                          return (
                            <td key={c.key} title={APPROVED_TIP}
                              style={{ ...cellStyle(vi, cellBg, false), cursor: editable ? 'pointer' : undefined }}
                              onClick={editable ? (e) => { e.stopPropagation(); toggleApproved(row.id); } : undefined}>
                              <Checkbox size="xs" checked={!!row.authorized} readOnly tabIndex={-1}
                                disabled={!editable} aria-label={`Approved: ${row.description}`}
                                styles={{ input: { cursor: editable ? 'pointer' : 'default' } }} />
                            </td>
                          );
                        }
                        return (
                          <td key={c.key} style={st} title={isActive ? undefined : cellText(c, row)}
                            onClick={editable ? (e) => { e.stopPropagation(); setActive({ id: row.id, key: c.key }); } : undefined}>
                            {isActive && isLens ? (
                              <ClsCellEditor
                                options={idx?.childrenAt(tier, parentTierId) ?? []}
                                value={readingId ? (idx?.ancestorAtTier(readingId, tier) ?? null) : null}
                                onCommit={(chosen) => commitCls(row.id, c.code ?? '', chosen ?? (tier > 1 ? parentTierId : null))}
                                onClose={() => setActive(null)} />
                            ) : isActive ? (
                              <CellEditor kind={kind!} initial={initial}
                                onCommit={(raw) => commitCell(row.id, c.key, raw)}
                                onCancel={() => setActive(null)} />
                            ) : vi === 0 ? (
                              <Group gap={6} wrap="nowrap" style={{ paddingLeft: depth ? depth * 16 + 18 : 0, overflow: 'hidden' }}>
                                {editing && canManage && (
                                  <Tooltip label="Delete this security" withArrow>
                                    <ActionIcon size="xs" color="red" variant="subtle"
                                      onClick={(e) => { e.stopPropagation(); setDeleteTarget(row); }}>
                                      <IconTrash size={13} />
                                    </ActionIcon>
                                  </Tooltip>
                                )}
                                {/* The text gives way to the badge (it shortens with "…"), never under it (HP-V4).
                                    The classification badges moved to their own column (HP-V5). */}
                                {c.key === CLS_STATUS_KEY ? statusBadge(row) : (
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flex: '1 1 auto' }}>{cellText(c, row)}</span>
                                )}
                                {row.is_benchmark && <Badge size="xs" color="azul" variant="light" style={{ flex: '0 0 auto' }}>BM</Badge>}
                              </Group>
                            ) : c.key === CLS_STATUS_KEY ? (
                              statusBadge(row)
                            ) : (
                              cellText(c, row)
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {displayed.length === 0 && (
                  <tr><td colSpan={Math.max(1, visibleCols.length)} style={{ padding: 16, textAlign: 'center' }}>
                    <Text size="sm" c="dimmed">
                      {allRows.length === 0 ? 'No securities loaded.' : 'No securities match these filters.'}
                    </Text>
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Stack>

      <DetailDrawer id={selectedId} onClose={() => setSelectedId(null)} />
      <CreateDrawer opened={createOpen} onClose={() => setCreateOpen(false)}
        onCreated={() => qc.invalidateQueries({ queryKey: ['securities'] })} />
      <ImportDrawer opened={importOpen} onClose={() => setImportOpen(false)}
        onImported={() => qc.invalidateQueries({ queryKey: ['securities'] })} />
      <Modal opened={usageAsk !== null} onClose={() => answerUsage(false)} size="lg" centered
        title="Un-approve securities that are held?">
        <Stack gap="sm">
          <Text size="sm">
            {usageAsk?.items.length === 1 ? 'This security is' : `These ${usageAsk?.items.length} securities are`} in
            a bucket and / or a client portfolio (latest positions). Saving sets Approved to No: the buckets will show
            {usageAsk?.items.length === 1 ? ' it' : ' them'} red and can still be used; trades can no longer buy {usageAsk?.items.length === 1 ? 'it' : 'them'}.
          </Text>
          <Table withTableBorder verticalSpacing={4}>
            <Table.Thead>
              <Table.Tr><Table.Th>Security</Table.Th><Table.Th>Buckets / models</Table.Th><Table.Th>Portfolios</Table.Th></Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {usageAsk?.items.map((u) => (
                <Table.Tr key={u.security_id}>
                  <Table.Td><Text size="xs">{u.name}</Text><Text size="xs" c="dimmed" ff="monospace">{u.isin} {u.currency}</Text></Table.Td>
                  <Table.Td><Text size="xs">{[...u.buckets.map((b) => b.name), ...(u.models ?? []).map((m) => `model ${m.name}`)].join(', ') || '—'}</Text></Table.Td>
                  <Table.Td>
                    <Tooltip withArrow multiline w={320} disabled={u.portfolios.length === 0}
                      label={u.portfolios.map((p) => `${p.label} (${p.as_of})`).join(' · ')}>
                      <Text size="xs">{u.portfolios.length || '—'}</Text>
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          {usageShown
            ? <WhereHeldList items={usageAsk?.items ?? []} />
            : null}
          <Group justify="space-between">
            {usageShown ? <span /> : (
              <Button size="xs" variant="light" leftSection={<IconList size={14} />} onClick={() => setUsageShown(true)}>
                Show the list</Button>
            )}
            <Group gap="xs">
              <Button variant="default" onClick={() => answerUsage(false)}>Cancel</Button>
              <Button color="red" onClick={() => answerUsage(true)}>Un-approve and save</Button>
            </Group>
          </Group>
        </Stack>
      </Modal>
      <Modal opened={deleteTarget !== null} onClose={() => setDeleteTarget(null)} title="Delete security">
        {deleteTarget && (
          <DeleteSecurityBody target={deleteTarget} deleting={deleting}
            onCancel={() => setDeleteTarget(null)} onDelete={handleDelete} />
        )}
      </Modal>
    </Container>
  );
}

/** The Delete security dialog's body. Model Portfolios M1 (Pedro 2026-10-26: "yes, with loud
 *  warning"): a security in a bucket cannot be deleted; the dialog checks first and says so in
 *  red, naming the buckets, with Delete greyed and its reason (the server refuses it anyway). */
function DeleteSecurityBody({ target, deleting, onCancel, onDelete }: {
  target: SecurityRow; deleting: boolean; onCancel: () => void; onDelete: () => void;
}) {
  const usageQ = useQuery({ queryKey: ['securities', 'usage', target.id], queryFn: () => securityUsageApi.of([target.id]),
    staleTime: 0, retry: 1 });
  // M-SEC: a bucket member, or a security held directly by a model.
  const uses = [...(usageQ.data?.items[0]?.buckets ?? []).map((b) => `bucket ${b.name}`),
    ...(usageQ.data?.items[0]?.models ?? []).map((m) => `model ${m.name}`)];
  // Fails closed (M1-DEL): if the check cannot run, Delete stays greyed and says why.
  const reason = usageQ.isFetching ? 'Checking whether a model holds this security…'
    : usageQ.isError ? 'Could not check whether a model holds this security; close this and try again.'
    : uses.length ? 'A security in a model or a bucket cannot be deleted; remove it there first.'
    : undefined;
  return (
    <Stack>
      {uses.length > 0 && (
        <Alert color="red" variant="filled" icon={<IconAlertTriangle size={18} />}
          title="This security is in a model and cannot be deleted">
          It is in {uses.join(', ')}. Remove it there first (Models); then it can be deleted here.
        </Alert>
      )}
      <Text size="sm">
        Delete <b>{target.description}</b> ({target.isin})? It is soft-deleted —
        the record and its history are kept and it can be restored later by an admin — but it
        leaves the securities list.
      </Text>
      <Group justify="flex-end">
        <Button variant="default" onClick={onCancel}>Cancel</Button>
        <DisabledTooltipButton color="red" onClick={onDelete} loading={deleting} disabledReason={reason}>Delete</DisabledTooltipButton>
      </Group>
    </Stack>
  );
}
